/**
 * Client-side photo-proof image processing. Mirrors `features/upload-reward-photo/reward-image.ts` (and
 * `features/upload-avatar-photo/avatar-image.ts`): the RAW camera file NEVER reaches the server. We
 * decode it, correct EXIF orientation, downscale so the longest edge is ≤ {@link PHOTO_PROOF_IMAGE_MAX_PX}
 * (preserving the original aspect — a chore photo isn't forced square, unlike an avatar), and re-encode
 * through a `<canvas>` to WebP. Re-encoding via canvas strips ALL EXIF/GPS/timestamp metadata for free
 * (the canvas only ever sees decoded pixels) — without this, a phone camera with location tagging on
 * (the common default whenever Location Services is enabled for the Camera app) would embed exactly
 * where a child was standing into a photo a household guardian later views (security checklist §9: "no
 * real name/email/precise location for children"). Only the processed WebP blob is uploaded.
 */

/** The longest-edge output cap (px) — a proof photo is viewed at up to ~70vh in a dialog; ample detail. */
export const PHOTO_PROOF_IMAGE_MAX_PX = 1024;

/** WebP quality for the re-encode — a good size/quality balance. */
const WEBP_QUALITY = 0.85;

/** A stable, translatable reason processing failed (the UI maps these to kind copy). */
export type PhotoProofImageError = 'decode-failed' | 'encode-failed';

export class PhotoProofImageProcessingError extends Error {
  constructor(public readonly reason: PhotoProofImageError) {
    super(reason);
    this.name = 'PhotoProofImageProcessingError';
  }
}

/** Decode a file into an `ImageBitmap` with EXIF orientation already applied (upright). */
async function decodeUpright(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    try {
      return await createImageBitmap(file);
    } catch {
      throw new PhotoProofImageProcessingError('decode-failed');
    }
  }
}

/** Promisified `canvas.toBlob` (WebP). Rejects with `encode-failed` if the encoder yields nothing. */
function canvasToWebp(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new PhotoProofImageProcessingError('encode-failed'));
      },
      'image/webp',
      WEBP_QUALITY,
    );
  });
}

/**
 * Process a camera-captured file into an upload-ready WebP blob: decode upright (EXIF) → downscale
 * longest edge ≤ {@link PHOTO_PROOF_IMAGE_MAX_PX} (aspect preserved, never upscale) → re-encode WebP
 * (metadata-stripped). Throws a {@link PhotoProofImageProcessingError} with a stable `reason` the caller
 * maps to kind copy. The caller is expected to have already validated the file's type/size (`PhotoCapture`'s
 * own `wrongType`/`tooLarge` checks run first).
 */
export async function processPhotoProofFile(file: File): Promise<Blob> {
  const bitmap = await decodeUpright(file);
  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > PHOTO_PROOF_IMAGE_MAX_PX ? PHOTO_PROOF_IMAGE_MAX_PX / longest : 1;
    const outW = Math.max(1, Math.round(bitmap.width * scale));
    const outH = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new PhotoProofImageProcessingError('encode-failed');
    ctx.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, 0, outW, outH);

    return await canvasToWebp(canvas);
  } finally {
    bitmap.close?.();
  }
}
